// demoWellProgram — the CALENDAR-DAY view of a demo well's drilling program.
//
// WHY THIS EXISTS. Every per-day report the demo needs — cost log, mud checks, drilling params —
// has to agree about the SAME well: how many days it took, which section was being drilled on day
// 9, whether that day was drilling or running casing. Generated independently they contradict each
// other, and a demo whose cost log says "cementing" on a day the time log says "drilling" is worse
// than a blank one.
//
// ── THIS IS A VIEW, NOT A MODEL ───────────────────────────────────────────────────────────────
//
// The durations come from `oilfieldTimeLog.buildSequence`, which transcribes the PO's own
// operational sequence (DECISIONS.md 2026-09-03 — ft/hr rates, survey cadence, fixed-step hours,
// "total drilling days of 18-30"). NOTHING here re-derives a rate, a duration or a depth. This
// module only folds that hour-resolution sequence into calendar days and names, per day, the
// section and the dominant activity.
//
// THE DEFECT THIS REPLACES, recorded because it is the expensive kind. An earlier version of this
// file carried its OWN ROP constants (surface 34 / intermediate 13 / curve 6.5 / lateral 23 m/hr)
// and its own flat-time and NPT model. It was written without knowing `oilfieldTimeLog.ts` existed
// — six hours after the PO had specified the same pad in detail. Two duration models for one pad is
// the exact drift CLAUDE.md's cross-implementation parity rule exists to prevent; the fix is
// deletion, not reconciliation. See DECISIONS.md 2026-09-03 for the correction.
//
// HOW FAR APART THEY WERE, with the command that says so rather than a remembered multiple. The
// deleted constants were surface 34 / intermediate 13 / curve 6.5 / lateral 23 m/hr; the PO's
// stated rates are 180 / 100 / 200 ft/hr for vertical / curve / lateral (he stated NO surface ROP,
// so surface cannot be compared at all). Ratios, `python3 -c` on `rate_ft / (rate_m * 3.2808)`:
//     intermediate  180/(13*3.2808)  = 4.22x
//     curve         100/(6.5*3.2808) = 4.69x
//     lateral       200/(23*3.2808)  = 2.65x
// An earlier draft of this comment said "roughly 2.5x" across the board, which is the LATERAL
// figure alone and understates the other two by about half. Corrected by the attribution review.
//
// UNIT MODEL: store-as-entered + UOM; convert on-the-fly (no SI normalization). Every depth this
// module emits is METRES MD, inherited verbatim from `oilfieldTimeLog`'s own depth stamps, which
// are metres because the generator's bores are (`depthUnit: "m"`). Every duration is HOURS. Those
// units are fixed by the source and are not user-entered, so there is no per-value UOM sibling to
// carry — the field docs below name the unit at each site (Charter Abs-3).
//
// DATA MODEL: no DDL table. This is an in-memory scheduling view consumed by report generators; it
// is never persisted. The reports it feeds ARE persisted and carry their own mappings.
//
// NO ENGINEERING MATH (Charter Abs-2). Bucketing hours into days and picking the longest-running
// activity is scheduling arithmetic over authored demo data. It computes no result an engineer
// would rely on and reads nothing back into the product.

import { buildSequence, type OilfieldWellGeometry } from "./oilfieldTimeLog";
import { localDay } from "../lib/date";

/** Deterministic [0,1) from a seed string — FNV-1a, no wall-clock or Math.random dependence.
 *
 *  WHY SEEDED HELPERS LIVE HERE. `testDataGenerator.ts` has 11 `Math.random` sites, so every
 *  reload produces different data: you cannot demo the same number twice, screenshot it, or write
 *  a test that asserts a value. These are the replacement — the same discipline
 *  `prebake_edm/anonymize.py` states as "no wall-clock/random dependence". */
export function seededUnit(...parts: (string | number)[]): number {
  let h = 0x811c9dc5;
  for (const ch of parts.join("|")) {
    h ^= ch.charCodeAt(0);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h >>> 8) / 0x01000000;
}

/** Deterministic integer in [min,max]. */
export const seededInt = (
  min: number,
  max: number,
  ...seed: (string | number)[]
): number => min + Math.floor(seededUnit(...seed) * (max - min + 1));

/** Deterministic float in [min,max], rounded to `decimals`. */
export const seededFloat = (
  min: number,
  max: number,
  decimals: number,
  ...seed: (string | number)[]
): number =>
  Number((min + seededUnit(...seed) * (max - min)).toFixed(decimals));

/** Deterministic pick from a list. */
export const seededPick = <T>(
  arr: readonly T[],
  ...seed: (string | number)[]
): T => arr[Math.floor(seededUnit(...seed) * arr.length) % arr.length]!;

// ── the day view ──────────────────────────────────────────────────────────────────────────────

/** Hole section, named by where the day's work sat relative to the shoes the casing program set. */
export type DemoSection =
  | "mobilization"
  | "surface"
  | "intermediate"
  | "curve"
  | "lateral";

export interface DemoDay {
  /** 1-based REPORT day of the well's drilling job, counted from MOVE-IN — report day 1 is the
   *  move-in day, and spud falls on day 4 (MOB_HOURS = 72 in `oilfieldTimeLog`).
   *
   *  NOT the drilling-day axis, and must never be fed to a `PlannedCurvePoint.day`, which is
   *  1-based from SPUD (CURVE-1, DECISIONS.md 2026-09-04). The two differ by the rig move — three
   *  days here — and both were called "day" without qualification, which is one of the four
   *  conflicting definitions that ruling resolved. This one keeps its own numbering because a
   *  daily report legitimately starts at move-in; what it may not do is share the word. */
  dayNo: number;
  /** ISO date (YYYY-MM-DD) in the job's own local offset. */
  date: string;
  /** Epoch ms at 00:00 of this day — the origin the per-day generators stamp from. */
  startMs: number;
  section: DemoSection;
  /** The coding-template code1 that consumed the most CRITICAL-PATH hours of the day. This is the
   *  time log's own vocabulary (SPUD, DRILLING, TRIPS, RUN_CASING, …), not a parallel one. */
  activity: string;
  /** Every code1 that touched the day, with its critical-path hours. The cost generator uses this
   *  to decide which services were on the ticket, not just the headline one. */
  hoursByCode: Record<string, number>;
  /** Hole depth at the START and END of the day, metres MD — read off the sequence's own depth
   *  stamps, never recomputed. Equal on a day that made no hole. */
  depthFrom: number;
  depthTo: number;
  /** True when no new hole was made — casing, cement, tripping, testing, waiting. */
  flat: boolean;
}

export interface DemoWellProgram {
  /** 0-based position on the pad, in the order the rig drills. */
  wellIndex: number;
  /** Epoch ms of move-in (job start) — day 1 hour 0. */
  originMs: number;
  spud: string;
  /** Rig release. */
  td: string;
  totalDays: number;
  totalMD: number;
  days: DemoDay[];
}

/** The cost-log `interval` for a section — the vocabulary COST_INTERVAL_OPTIONS already offers.
 *  The curve and lateral are drilled in the production hole, so both map to "Production". */
export const INTERVAL_FOR_SECTION: Record<DemoSection, string> = {
  mobilization: "N/A",
  surface: "Surface",
  intermediate: "Intermediate",
  curve: "Production",
  lateral: "Production",
};

/** The cost-log `phase` for a section. */
export const PHASE_FOR_SECTION: Record<DemoSection, string> = {
  mobilization: "Mobilization",
  surface: "Drilling",
  intermediate: "Drilling",
  curve: "Drilling",
  lateral: "Drilling",
};

const MS_PER_DAY = 86_400_000;

/** The report-day date for day `d`, as `YYYY-MM-DD`.
 *
 *  LOCAL, via `localDay` — the repo-wide convention. `lib/date.ts` states it plainly: "Report day is
 *  the operator's LOCAL calendar day, NOT a UTC slice. Use localDay() everywhere a daily-report /
 *  survey / timeline day-date is CREATED", and it records a MIGRATION away from UTC day-slicing.
 *
 *  AN EARLIER VERSION OF THIS FUNCTION RENDERED UTC, and it split the well's own record. Job
 *  `startDate` / `spudDate` / `endDate` come from `oilfieldTimeLog` through `toOffsetISO`, which is
 *  local; the timeline's day-slice uses `localDay`; only the cost log and mud log went UTC. Measured
 *  under TZ=America/Chicago: PH-1H's job header read 2018-03-04 while its first cost log read
 *  2018-03-05. `useDailyReportPage`'s `?at=` join keys on the local day, so clicking a day-1
 *  timeline block would have found no matching report and SCAFFOLDED A SECOND ONE — the day-card
 *  duplicate this repo has already paid for once.
 *
 *  BUILD REPRODUCIBILITY IS SOLVED BY PINNING THE ZONE, NOT BY CHANGING THE CALENDAR. The pad is in
 *  Loving County, Texas, so its report days are Texas days; `build_demo_pad.ts` refuses to run
 *  without `TZ` pinned. Two builders in different zones then produce identical output because they
 *  are both rendering the rig's local day, which is what the data means.
 *
 *  Walking the calendar rather than adding 86,400,000 ms also keeps the dates distinct and
 *  consecutive across a DST transition, where a local day is 23 or 25 hours long. */
function calendarDate(originMs: number, d: number): string {
  const base = new Date(originMs);
  base.setHours(0, 0, 0, 0);
  base.setDate(base.getDate() + d);
  return localDay(base);
}

/** Which hole section a depth sits in, per the shoes the casing program actually set. */
function sectionAtDepth(
  depth: number,
  geom: OilfieldWellGeometry,
): DemoSection {
  if (depth <= geom.surfaceShoeMD) return "surface";
  if (depth <= geom.intermediateShoeMD) return "intermediate";
  if (depth <= geom.landingMD) return "curve";
  return "lateral";
}

/**
 * Fold one well's ratified hour-resolution sequence into calendar days.
 *
 * @param geometry    the shoes and trajectory — the SAME object handed to `generateOilfieldTimeLog`,
 *                    so the days and the time log cannot disagree about where the shoes are.
 * @param previousWell name of the well the rig walked off (the sequence's move-in comment).
 * @param originMs    epoch ms of hour 0 (move-in). The caller owns the pad's placement on the
 *                    calendar; this module never picks a date.
 * @param wellIndex   0-based pad position, carried through for seeding downstream generators.
 */
export function demoWellProgram(
  geometry: OilfieldWellGeometry,
  previousWell: string,
  originMs: number,
  wellIndex = 0,
): DemoWellProgram {
  const { specs, spudH, releaseH } = buildSequence(geometry, previousWell);

  // COVER EVERY BLOCK, not merely up to `releaseH`. Daily rig-service blocks are pushed at
  // `spudH + d*24 + jitter`, which can run past the release hour — and a day window that stops at
  // `releaseH` dropped that work silently (measured: 3 of 3,096 geometries lost 1.3 h of RIG_INSP).
  let lastSpecH = releaseH;
  for (const sp of specs) if (sp.endH > lastSpecH) lastSpecH = sp.endH;
  const totalDays = Math.max(1, Math.ceil(lastSpecH / 24));
  const days: DemoDay[] = [];

  for (let d = 0; d < totalDays; d++) {
    const dayStartH = d * 24;
    const dayEndH = dayStartH + 24;
    const isLastDay = d === totalDays - 1;
    const startMs = originMs + d * MS_PER_DAY;

    // Critical-path hours by code1, clipped to this day. Parallel-lane work (surveys hung beside
    // drilling, safety meetings) is excluded from the ranking so it cannot outvote the operation
    // that actually held the rig — but it is still counted in hoursByCode for the cost ticket.
    const hoursByCode: Record<string, number> = {};
    let bestCpCode = "";
    let bestCpHours = 0;
    let bestAnyCode = "";
    let bestAnyHours = 0;
    let deepest: number | null = null;
    let shallowestStart: number | null = null;
    let earliestStartH = Infinity;

    for (const s of specs) {
      const lo = Math.max(s.startH, dayStartH);
      const hi = Math.min(s.endH, dayEndH);
      // A milestone is instantaneous (startH === endH); include it when it falls in the day, but it
      // contributes no hours and so can never become the day's headline activity.
      // A milestone is instantaneous. The half-open window `[start, end)` is right for every day
      // BUT THE LAST, where a milestone landing exactly on the closing boundary would fall through
      // every window and disappear. `RIG_RELEASE` is emitted at exactly `releaseH`, so whenever
      // `releaseH` was a multiple of 24 the well lost its release milestone — and with it the
      // demobilization cost line (measured: 11 of 3,096 geometries).
      const isMilestoneToday =
        s.startH === s.endH &&
        s.startH >= dayStartH &&
        (s.startH < dayEndH || (isLastDay && s.startH <= dayEndH));
      if (hi <= lo && !isMilestoneToday) continue;

      const hours = Math.max(0, hi - lo);
      hoursByCode[s.code1] = (hoursByCode[s.code1] ?? 0) + hours;
      if (s.cp && hours > bestCpHours) {
        bestCpHours = hours;
        bestCpCode = s.code1;
      }
      if (hours > bestAnyHours) {
        bestAnyHours = hours;
        bestAnyCode = s.code1;
      }

      // DEPTH IS CLIPPED TO THE DAY, NOT INHERITED WHOLE. A drilling block that spans midnight must
      // contribute only the hole it made BEFORE midnight; taking its full `d1` credited a single
      // day with more lateral than 24 h at the PO's stated 200 ft/hr could produce, which is the
      // invariant `demoWellProgram.test.ts` now asserts. Drilled depth is linear in time within a
      // block, so interpolating across the clipped window is exact for this data.
      if (s.d1 !== null) {
        const span = s.endH - s.startH;
        if (s.d0 === null) {
          // No start depth means no gradient to interpolate along, so the block's end depth can
          // only be credited to the day the block actually ENDS in. Substituting `d1` for the
          // missing `d0` made the interpolation a no-op and handed a partially-clipped block its
          // full end depth — the very defect the interpolation exists to prevent.
          if (s.endH <= dayEndH && (deepest === null || s.d1 > deepest))
            deepest = s.d1;
        } else {
          const end =
            span > 0 ? s.d0 + ((s.d1 - s.d0) * (hi - s.startH)) / span : s.d1;
          if (deepest === null || end > deepest) deepest = end;
        }
      }
      // The day's OPENING depth is the depth of its EARLIEST block, not the shallowest one a trip
      // happens to carry — `min(d0)` reintroduced the backwards-stepping defect on the first day
      // with hole, where no previous frontier exists to override it.
      if (s.d0 !== null && s.startH < earliestStartH) {
        earliestStartH = s.startH;
        shallowestStart = s.d0;
      }
    }

    // DEPTH IS A RUNNING FRONTIER, never a per-day minimum. Taking `min(d0)` across the day's
    // blocks read a TRIP's shallow start as the day's starting depth, so the curve stepped
    // BACKWARDS: a trip block's `d0` sits far above the hole it was tripping out of. The hole only
    // gets deeper: a
    // day starts where the previous ended, and a day with no depth stamp at all (mobilization, rig
    // down) holds that depth rather than dropping to surface.
    // The frontier starts at the CONDUCTOR shoe, not at zero: the conductor is driven before the
    // rig arrives, so the first drilled day makes hole from there. Seeding it at 0 credited day 4
    // with 610 m when only 564 m was drilled — the kind of error that shows up as a wrong $/m.
    // The critical-path operation is the day's headline. Falling back to the PREVIOUS day's
    // activity invented one — a day holding 24 h of RIG_DOWN reported WIRELINE_LOGS, because rig
    // down is not flagged critical path. Fall back to whatever actually consumed the most hours;
    // and if the day is genuinely empty, record the placeholder in `hoursByCode` too, so the
    // invariant "the headline activity appears in the day's own hours" cannot be violated.
    const headline = bestCpCode || bestAnyCode || "SVC_RIG";
    if (!(headline in hoursByCode)) hoursByCode[headline] = 0;

    const prev = days.length > 0 ? days[days.length - 1]! : undefined;
    // Mobilization days sit at depth 0, so "has a previous day" is not the same as "the frontier
    // has been established". Until the first day that actually has hole, take the shallowest depth
    // the sequence itself stamps.
    const from =
      prev && prev.depthTo > 0
        ? prev.depthTo
        : (shallowestStart ?? prev?.depthTo ?? 0);
    const to = Math.max(from, deepest ?? from);

    days.push({
      dayNo: d + 1,
      date: calendarDate(originMs, d),
      startMs,
      section: dayEndH <= spudH ? "mobilization" : sectionAtDepth(to, geometry),
      activity: headline,
      hoursByCode,
      depthFrom: Math.round(from),
      depthTo: Math.round(to),
      flat: to <= from,
    });
  }

  return {
    wellIndex,
    originMs,
    spud: calendarDate(originMs, Math.floor(spudH / 24)),
    // TD IS THE LAST DAY'S OWN DATE, not the release instant rendered independently. Rendering
    // `originMs + releaseH` as a date disagreed with `days.at(-1).date` for 270 of 3,096
    // geometries (8.7%), because the release hour falls inside the last day's window but can land
    // on the next calendar date whenever the origin is not local midnight — which, at the real
    // call site, it never is.
    td: days[days.length - 1]!.date,
    totalDays,
    totalMD: geometry.totalMD,
    days,
  };
}
